/**
 * @fileoverview Type-to-confirm modal for full-sync (delete) import commits.
 * @module import-editor/runFlow/DeleteConfirmModal
 *
 * Responsibilities:
 * - Block a full-sync commit behind an explicit checkbox + typed "DELETE"
 *   confirmation so destructive imports cannot be fired accidentally.
 *
 * Dependencies:
 * - React only.
 *
 * Architecture:
 * - Presentation-only modal; the run flow owns when it opens and what a
 *   confirmed commit does. Extracted from tenant-web's ImportWizardContainer
 *   during the Phase 2A run-flow migration (G-002).
 *
 * Notes:
 * - Shown only when the host enables `requireFullSyncDeleteConfirm` and the
 *   run's import behavior is 'full-sync'.
 */

import { useState } from 'react';

/**
 * @contract DeleteConfirmModalProps
 *
 * Purpose:
 * - Configure the full-sync delete confirmation modal.
 *
 * Fields:
 * - count: number of records that will be archived
 * - onConfirm: user checked the box AND typed DELETE
 * - onCancel: dismiss without committing
 */
export interface DeleteConfirmModalProps {
  count: number;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * @component DeleteConfirmModal
 *
 * Purpose:
 * - Require an explicit two-step confirmation before a full-sync commit.
 *
 * Props:
 * - DeleteConfirmModalProps.
 *
 * Behavior:
 * - Confirm stays disabled until the checkbox is ticked and the user types
 *   exactly DELETE.
 *
 * Data Source:
 * - Local component state only.
 *
 * Constraints:
 * - No data access; no side effects beyond the callbacks.
 *
 * Performance:
 * - Trivial render; no memoization needed.
 */
export function DeleteConfirmModal({ count, onConfirm, onCancel }: DeleteConfirmModalProps) {
  const [typed, setTyped] = useState('');
  const [checked, setChecked] = useState(false);
  const canConfirm = checked && typed === 'DELETE';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">⚠️</span>
          <h3 className="text-base font-semibold text-red-700">Confirm Delete Import</h3>
        </div>
        <p className="text-sm text-gray-700">
          This will permanently archive <strong>{count.toLocaleString()} record{count !== 1 ? 's' : ''}</strong> from the database. This action cannot be undone.
        </p>
        <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-700">
          <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} className="mt-0.5 w-4 h-4 shrink-0" />
          I understand this will permanently archive {count.toLocaleString()} record{count !== 1 ? 's' : ''}
        </label>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Type <strong>DELETE</strong> to confirm</label>
          <input type="text" value={typed} onChange={e => setTyped(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm font-mono focus:outline-hidden focus:ring-1 focus:ring-red-500"
            placeholder="DELETE" />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel}
            className="px-4 py-1.5 rounded-md text-xs font-medium border border-gray-300 text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={!canConfirm}
            className="px-4 py-1.5 rounded-md text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
            Confirm Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteConfirmModal;
